import { FC, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { DatePicker } from '../../../common/form/DatePicker';
import { WorkplaceAutocomplete } from '../../../common/form/WorkplaceAutocomplete';
import { EstablishmentAutocomplete } from '../../../common/form/EstablishmentAutocomplete';
import { SupplierAutocomplete } from '../../../common/form/SupplierAutocomplete';
import { AnalyticsQuickPickRow } from '../../components/AnalyticsQuickPickRow';
import { DateRangeRow, FilterBarRoot, StatusToggleRow } from '../../components/AnalyticsFilterBarStyles';
import { Section, SectionLabel } from '../../components/AnalyticsSharedStyles';
import { InvitationTypeFilter } from '@api/models/enums/InvitationTypeFilter';
import { VisitationFormValues, VisitationStatus } from './visitationTypes';
import { VisitationEmployeeAutocomplete } from './VisitationEmployeeAutocomplete';
import { VisitationActivityAutocomplete } from './VisitationActivityAutocomplete';
import { EmployeeAutocomplete } from '../../../common/form/EmployeeAutocomplete';
import { useAppSelector } from '../../../../store';
import { selectIdentityCompanyId } from '../../../../store/entities/identity';
import { supplierEmployeeService } from '@api/services/services';
import { SupplierEmployeeDto } from '@api/models/suppliers/SupplierEmployeeDto';
import { getFirstLastNameObj } from '../../../../utils/format';

const STATUS_OPTIONS: { value: VisitationStatus; labelKey: string }[] = [
  { value: 'ALL', labelKey: 'analytics.filter.status.all' },
  { value: 'INSIDE', labelKey: 'analytics.filter.status.inside' },
  { value: 'LEFT', labelKey: 'analytics.filter.status.left' },
];

const INVITATION_FILTER_OPTIONS: { value: InvitationTypeFilter; labelKey: string }[] = [
  { value: InvitationTypeFilter.All, labelKey: 'analytics.filter.allInvitations' },
  { value: InvitationTypeFilter.RegularOnly, labelKey: 'analytics.filter.supplierInvitations' },
  { value: InvitationTypeFilter.OneTimeOnly, labelKey: 'analytics.filter.oneTimeVisits' },
];

export const VisitationFilterBar: FC = () => {
  const { t } = useTranslation();
  const { values, setFieldValue, setValues } = useFormikContext<VisitationFormValues>();
  const companyId = useAppSelector(selectIdentityCompanyId);

  const establishmentFilterIds = values.establishment.length ? values.establishment.map((e) => e.id) : undefined;

  const supplierIds = values.supplier.map((s) => s.id!);

  // Hydrate URL-restored / drilldown-synthetic employees (supplierId === undefined) in the background.
  // getById failures surface as global error toasts via BaseService.makeRequest — accepted for rare URL-restore failures.
  const attemptedHydrationIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!companyId) return;
    const idsToHydrate = values.employee.filter((e) => e.supplierId === undefined && !attemptedHydrationIdsRef.current.has(e.id)).map((e) => e.id);
    if (idsToHydrate.length === 0) return;

    idsToHydrate.forEach((id) => attemptedHydrationIdsRef.current.add(id));

    let cancelled = false;
    Promise.all(
      idsToHydrate.map((id) =>
        supplierEmployeeService
          .getById(id)
          .then((r) => r.data)
          .catch(() => null),
      ),
    ).then((dtos) => {
      if (cancelled) return;
      const byId = new Map(dtos.filter((d): d is SupplierEmployeeDto => d?.id !== undefined).map((d) => [d.id!, d]));
      if (byId.size === 0) return;
      setValues((prev) => ({
        ...prev,
        employee: prev.employee.map((e) => {
          if (e.supplierId !== undefined) return e;
          const dto = byId.get(e.id);
          return dto ? { id: e.id, name: getFirstLastNameObj(dto), supplierId: dto.supplier?.id } : e;
        }),
      }));
    });

    return () => {
      cancelled = true;
    };
  }, [companyId, values.employee, setValues]);

  return (
    <FilterBarRoot>
      <Section>
        <SectionLabel>{t('analytics.filter.entryTimeLabel')}</SectionLabel>
        <AnalyticsQuickPickRow fromName="entryTimeStart" toName="entryTimeEnd" />
        <DateRangeRow>
          <DatePicker name="entryTimeStart" label={t('analytics.filter.from')} />
          <DatePicker name="entryTimeEnd" label={t('analytics.filter.to')} />
        </DateRangeRow>
      </Section>

      <Section>
        <SectionLabel>{t('analytics.filter.exitTimeLabel')}</SectionLabel>
        <AnalyticsQuickPickRow fromName="exitTimeStart" toName="exitTimeEnd" />
        <DateRangeRow>
          <DatePicker name="exitTimeStart" label={t('analytics.filter.from')} />
          <DatePicker name="exitTimeEnd" label={t('analytics.filter.to')} />
        </DateRangeRow>
      </Section>

      <Section>
        <SectionLabel>{t('analytics.filter.whereLabel')}</SectionLabel>
        <EstablishmentAutocomplete multiple name="establishment" label={t('analytics.filter.establishments')} />
        <WorkplaceAutocomplete multiple name="workplace" label={t('analytics.filter.workplaces')} estabilishments={establishmentFilterIds} />
        <VisitationActivityAutocomplete multiple name="activity" label={t('analytics.filter.activities')} />
        <EmployeeAutocomplete multiple name="visitedPerson" label={t('analytics.filter.visitedPersons')} />
      </Section>

      <Section>
        <SectionLabel>{t('analytics.filter.suppliers')}</SectionLabel>
        <SupplierAutocomplete multiple name="supplier" label={t('analytics.filter.suppliers')} />
        <VisitationEmployeeAutocomplete multiple name="employee" label={t('analytics.filter.employee')} supplierIds={supplierIds} />
      </Section>

      <Section>
        <SectionLabel>{t('analytics.filter.statusLabel')}</SectionLabel>
        <StatusToggleRow>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={values.status}
            onChange={(_, next: VisitationStatus | null) => {
              if (next) setFieldValue('status', next);
            }}
          >
            {STATUS_OPTIONS.map((opt) => (
              <ToggleButton key={opt.value} value={opt.value}>
                {t(opt.labelKey)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </StatusToggleRow>
      </Section>

      <Section>
        <SectionLabel>{t('analytics.filter.invitationKindLabel')}</SectionLabel>
        <StatusToggleRow>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={values.invitationFilter}
            onChange={(_, next: InvitationTypeFilter | null) => {
              if (next) setFieldValue('invitationFilter', next);
            }}
          >
            {INVITATION_FILTER_OPTIONS.map((opt) => (
              <ToggleButton key={opt.value} value={opt.value}>
                {t(opt.labelKey)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </StatusToggleRow>
      </Section>
    </FilterBarRoot>
  );
};
